import React, { useEffect, useMemo, useState } from "react";
import "./DeliveredMessages.css";
import Table from "../../../components/reusable-table/Table";
import DeliveredMessagesModal from "./modals/DeliveredMessagesModal";
import Input from "../../../components/reusable-input/Input";
import { SearchIcon } from "../../../icons/reactIcons";
import SendMessage from "./modals/SendMessage";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import { getCmsMessages } from "../../../redux/slices/cmsMessageSlice";
import { fetchUserList } from "../../../redux/slices/userListSlice";
import { formatDateTime } from "../../../utils/formatDateTime";
import { fetchGroups } from "../../../redux/slices/userGroupSlice";

const columns = [
  { header: "Message", accessor: "message" },
  { header: "To", accessor: "to" },
  { header: "Created By", accessor: "createdBy" },
];

const LIMIT = 30;

const DeliveredMessages: React.FC = () => {
  const dispatch = useAppDispatch();

  const { list, loading } = useAppSelector((state) => state.cmsMessages);

  const [selectedMessage, setSelectedMessage] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);

  const adminDetail = JSON.parse(localStorage.getItem("adminDetail") || "{}");
  const userRole = adminDetail.role || "";
  const isStudyTeamUser = userRole === "study team";

  const [activeTab, setActiveTab] = useState<"admin" | "studyteam">(
    isStudyTeamUser ? "studyteam" : "admin",
  );

  const [adminPage, setAdminPage] = useState(1);
  const [studyTeamPage, setStudyTeamPage] = useState(1);

  const fetchAllData = (pageNumber = 1) => {
    dispatch(
      getCmsMessages({
        type: "list",
        pageId: pageNumber,
        limit: LIMIT,
      }),
    );

    dispatch(
      fetchUserList({
        pageId: 1,
        limit: 30,
        status: "active",
      }),
    );

    dispatch(fetchGroups());
  };

  useEffect(() => {
    fetchAllData(adminPage);
  }, [dispatch, adminPage]);

  useEffect(() => {
    fetchAllData(studyTeamPage);
  }, [dispatch, studyTeamPage]);

  // -----------------------------
  // SPLIT GROUPS
  // -----------------------------
  const adminGroups = useMemo(
    () => list?.filter((group) => group.roleName === "admin") || [],
    [list],
  );

  const studyTeamGroups = useMemo(
    () =>
      list?.filter((group) => group.roleName?.toLowerCase() === "study team") ||
      [],
    [list],
  );

  // -----------------------------
  // COUNT USING total_messages
  // -----------------------------
  const adminMessageCount = useMemo(
    () =>
      adminGroups.reduce(
        (total, group) => total + Number(group.totalMessages || 0),
        0,
      ),
    [adminGroups],
  );

  const studyTeamMessageCount = useMemo(
    () =>
      studyTeamGroups.reduce(
        (total, group) => total + Number(group.totalMessages || 0),
        0,
      ),
    [studyTeamGroups],
  );

  // -----------------------------
  // TRANSFORM DATA
  // -----------------------------
  const transformData = (groups: any[]) => {
    const flattened: any[] = [];

    groups.forEach((group) => {
      group?.messages?.forEach((msg: any) => {
        flattened.push({
          message: msg.message,
          fullMessage: msg,
          to:
            msg.users?.length > 1
              ? `${msg.users[0]?.userName}, + ${msg.users.length - 1} ${msg.users?.length === 2 ? "User" : "Users"}`
              : msg.users?.[0]?.userName || "-",
          createdBy: (
            <div className="merged-cell">
              <div className="merged-label">{msg.createdBy}</div>
              <div className="merged-date">
                {formatDateTime(msg.createdDate)}
              </div>
            </div>
          ),
        });
      });
    });

    return flattened;
  };

  const adminMessages = useMemo(
    () => transformData(adminGroups),
    [adminGroups],
  );

  const studyTeamMessages = useMemo(
    () => transformData(studyTeamGroups),
    [studyTeamGroups],
  );

  const filteredData = useMemo(() => {
    const source = activeTab === "admin" ? adminMessages : studyTeamMessages;

    if (!searchTerm.trim()) return source;

    return source.filter((item) =>
      item.message.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm, activeTab, adminMessages, studyTeamMessages]);

  const totalRecords =
    activeTab === "admin" ? adminMessageCount : studyTeamMessageCount;

  const currentPage = activeTab === "admin" ? adminPage : studyTeamPage;

  return (
    <div className="delivered-messages">
      <div className="Common-header-area">
        <h2 className="delivered-title">Delivered Messages</h2>

        <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
          <Input
            type="text"
            placeholder="Search"
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            search
            startIcon={<SearchIcon fontSize={"20px"} />}
            removeBottomMargin
          />

          <button
            className="user-groups-btn"
            onClick={() => setIsSendModalOpen(true)}
          >
            Send Message
          </button>
        </div>
      </div>

      <div className="delivered-messages-headerActions">
        {!isStudyTeamUser && (
          <div className="dm-tabs">
            <button
              className={`dm-tab ${activeTab === "admin" ? "active" : ""}`}
              onClick={() => setActiveTab("admin")}
            >
              Admin <span className="tab-count">{adminMessageCount}</span>
            </button>

            <button
              className={`dm-tab ${activeTab === "studyteam" ? "active" : ""}`}
              onClick={() => setActiveTab("studyteam")}
            >
              Study Team{" "}
              <span className="tab-count">{studyTeamMessageCount}</span>
            </button>
          </div>
        )}
      </div>

      <Table
        rounded={isStudyTeamUser}
        columns={columns}
        data={isStudyTeamUser ? studyTeamMessages : filteredData}
        rowsPerPage={LIMIT}
        loading={loading}
        serverSidePagination
        totalRecords={totalRecords}
        onRowClick={(row) => setSelectedMessage(row.fullMessage)}
        onPageChange={(newPage) => {
          if (activeTab === "admin") {
            setAdminPage(newPage);
          } else {
            setStudyTeamPage(newPage);
          }
        }}
      />

      <DeliveredMessagesModal
        isOpen={!!selectedMessage}
        onClose={() => setSelectedMessage(null)}
        messageData={selectedMessage}
      />

      <SendMessage
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        onSuccess={() => {
          setAdminPage(1);
          setStudyTeamPage(1);
          fetchAllData(1);
        }}
      />
    </div>
  );
};

export default DeliveredMessages;
